import { useState } from "react"
import { ChevronDownIcon } from "@heroicons/react/24/outline"
import { motion } from "framer-motion"
import Image from "aldoo-ra/Image"
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import MenuGroup from "aldoo-ra/MenuGroup"
import Copyright from "../Copyright"
import PoweredBy from "../PoweredBy"
import { navigate } from "vike/client/router"
import { usePageContext } from "vike-react/usePageContext"
import PubSub from "pubsub-js"

const GROUP_NAMES = ["FooterGroupProduct", "FooterGroupCompany", "FooterGroupSupport"]

// Reads an "open" parameter off a menu item's link (e.g. open: "customer-support").
function getOpenParam(item) {
  const linkParams = item.link?.[0]?.content?.parameters || item.link?.[0]?.parameters
  if (linkParams?.open) return linkParams.open
  const p = item.parameters
  if (!p) return null
  if (Array.isArray(p)) return p.find((e) => e.key === "open")?.value || null
  return p.open || null
}

function scrollToId(id, currentPath) {
  if (currentPath !== "/") {
    navigate("/")
    const start = Date.now()
    const poll = setInterval(() => {
      const el = document.getElementById(id)
      if (el) {
        clearInterval(poll)
        el.scrollIntoView({ behavior: "smooth" })
      } else if (Date.now() - start > 3000) {
        clearInterval(poll)
      }
    }, 50)
  } else {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" })
  }
}


const FooterAccordionSection = ({ groupName, isOpen, onToggle, onOpenContactSales, currentPath }) => {
  const itemText = "text-white/70 hover:text-white"
  const labelCls = "text-white/90 group-hover:text-white"
  const chevronCls = "text-white/60 group-hover:text-white/90"

  return (
    <div className="border-0 border-b py-1 hover:border-0 hover:border-b border-bordered hover:border-bordered">
      <MenuGroup
        name={groupName}
        renderAs="vlist"
        styleOverrides={{
          menu: "hidden",
          menu_column: "hidden",
        }}
        rootItemRender={(menuItem) => (
          <button
            onClick={onToggle}
            className="group flex justify-between items-center w-full text-left focus:outline-none py-3"
          >
            <span className={`text-sm font-semibold tracking-widest uppercase ${labelCls}`}>
              {menuItem.label}
            </span>
            <ChevronDownIcon
              className={`w-6 h-6 transition-transform duration-300 ${chevronCls} ${isOpen ? "rotate-180" : ""}`}
            />
          </button>
        )}
      />

      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <div className="!text-sm mt-0">
          <MenuGroup
            name={groupName}
            renderAs="vlist"
            skipRootItem
            itemsAsLinks={false}
            styleOverrides={{
              menu_column: "hidden",
              menu_item: "px-0 my-4",
            }}
            menuItemRender={(item) => {
              const url = item.link?.[0]?.url
              const openParam = getOpenParam(item)
              if (openParam) {
                return (
                  <button onClick={() => PubSub.publish(`open:${openParam}`)}>
                    <Typography variant="footer_item" className={itemText}>{item.label}</Typography>
                  </button>
                )
              }
              if (url && /#contact/.test(url)) {
                return (
                  <button onClick={onOpenContactSales}>
                    <Typography variant="footer_item" className={itemText}>{item.label}</Typography>
                  </button>
                )
              }
              const isExternal = url?.startsWith("http://") || url?.startsWith("https://")
              const isHashNav = !isExternal && (url?.startsWith("#") || url?.startsWith("/#"))
              return (
                <a
                  href={url}
                  {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  onClick={(e) => {
                    if (isHashNav) {
                      e.preventDefault()
                      const id = url.split("#")[1]
                      scrollToId(id, currentPath)
                    }
                  }}
                >
                  <Typography variant="footer_item" className={itemText}>
                    {item.label.startsWith("Aldoo ")
                      ? <><strong>Aldoo</strong>{item.label.slice(5)}</>
                      : item.label}
                  </Typography>
                </a>
              )
            }}
          />
        </div>
      </motion.div>
    </div>
  )
}

export default function FooterMobile({ onOpenContactSales }) {
  const texts = TextBundle({ id: "transfer_slogan" })
  const [openGroup, setOpenGroup] = useState(null)
  const pageContext = usePageContext()
  const currentPath = pageContext?.urlPathname || (typeof window !== "undefined" ? window.location.pathname : "/")

  return (
    <footer className="relative bg-footer pt-10">
      <div className="w-full px-px_sm">
        {/* Brand */}
        <div className="flex flex-col items-center gap-4 mb-4">
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault()
              navigate("/")
            }}
            className="flex items-center gap-3 shrink-0 mt-3"
          >
            <Image
              id="aldoo_transfer_logo_icon"
              className="h-11 w-auto object-contain"
            />

            <div className="flex flex-col mb-2">
              <span className="text-text font-bold text-2xl tracking-tight">
                Aldoo <span className="text-primary">Transfer</span>
              </span>

              <Typography 
                variant="txt"
                className="text-text leading-none"
              >
                {texts?.logo_slogan}
              </Typography>
            </div>
          </a>

          <MenuGroup
            renderAs={"hlist"}
            name="StoreButtons"
            itemsAsLinks={false}
            styleOverrides={{
              menu_item: "px-0 my-0"
            }}
            
            menuItemRender={(item) => {
              const url = item.link?.[0]?.url
              return (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="w-[150px]" style={{ aspectRatio: "220/65" }}>
                    <img
                      src={item.image}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </a>
              )
            }}
          />
        </div>

        {/* Accordion link groups */}
        <div className="w-full">
          {GROUP_NAMES.map((name) => (
            <FooterAccordionSection
              key={name}
              groupName={name}
              isOpen={openGroup === name}
              onToggle={() => setOpenGroup(openGroup === name ? null : name)}
              onOpenContactSales={onOpenContactSales}
              currentPath={currentPath}
            />
          ))}
        </div>

        {/* Bottom */}
        <div className="flex flex-col gap-4 items-center p-6">
          <Copyright />
          <PoweredBy />
        </div>
      </div>
    </footer>
  )
}
